import { useEffect, useRef, useState } from 'react'
import { showErrorToast } from '../utils/toast'

/**
 * Hook de sécurité pour empêcher les triches pendant le quiz
 * @param {boolean} isActive - Si le quiz est en cours
 * @param {Function} onViolation - Callback appelé en cas de violation détectée
 * @returns {Object} - État et fonctions de sécurité
 */
export function useQuizSecurity(isActive = false, onViolation = null) {
  const [tabSwitches, setTabSwitches] = useState(0)
  const [warnings, setWarnings] = useState(0)
  const [isBlurred, setIsBlurred] = useState(false)
  const violationsRef = useRef(0)
  const blurTimeRef = useRef(null)
  const warningTimeoutRef = useRef(null)

  // Désactiver le clic droit
  useEffect(() => {
    if (!isActive) return

    const handleContextMenu = (e) => {
      e.preventDefault()
      violationsRef.current++
      showErrorToast({ 
        message: 'Le clic droit est désactivé pendant le quiz' 
      })
      if (onViolation) {
        onViolation('context_menu')
      }
      return false
    }

    // Désactiver les raccourcis clavier dangereux
    const handleKeyDown = (e) => {
      // Bloquer F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'L\'inspection du code est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('devtools_shortcut')
        }
        return false
      }

      // Bloquer Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.key === 'I') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'L\'inspection du code est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('devtools_shortcut')
        }
        return false
      }

      // Bloquer Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.key === 'J') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'La console est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('console_shortcut')
        }
        return false
      }

      // Bloquer Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && e.key === 'C') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'L\'inspection est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('inspect_shortcut')
        }
        return false
      }

      // Bloquer Ctrl+U (View Source)
      if (e.ctrlKey && e.key === 'u') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'L\'affichage du code source est désactivé pendant le quiz' 
        })
        if (onViolation) {
          onViolation('view_source')
        }
        return false
      }

      // Bloquer Ctrl+S (Save)
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault()
        showErrorToast({ 
          message: 'La sauvegarde est désactivée pendant le quiz' 
        })
        return false
      }

      // Bloquer Ctrl+P (Print)
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'L\'impression est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('print')
        }
        return false
      }

      // Bloquer Ctrl+C (Copy) - mais permettre dans les champs de texte
      if (e.ctrlKey && e.key === 'c' && !['TEXTAREA', 'INPUT'].includes(e.target.tagName)) {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'La copie est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('copy')
        }
        return false
      }

      // Bloquer Ctrl+A (Select All) - mais permettre dans les champs de texte
      if (e.ctrlKey && e.key === 'a' && !['TEXTAREA', 'INPUT'].includes(e.target.tagName)) {
        e.preventDefault()
        violationsRef.current++
        showErrorToast({ 
          message: 'La sélection est désactivée pendant le quiz' 
        })
        if (onViolation) {
          onViolation('select_all')
        }
        return false
      }
    }

    // Désactiver la sélection de texte
    const handleSelectStart = (e) => {
      // Permettre la sélection dans les champs de texte
      if (['TEXTAREA', 'INPUT'].includes(e.target.tagName)) {
        return true
      }
      e.preventDefault()
      return false
    }

    // Désactiver le glisser-déposer
    const handleDragStart = (e) => {
      e.preventDefault()
      return false
    }

    // Désactiver la copie via le menu contextuel
    const handleCopy = (e) => {
      // Permettre la copie dans les champs de texte
      if (['TEXTAREA', 'INPUT'].includes(e.target.tagName)) {
        return true
      }
      e.preventDefault()
      violationsRef.current++
      showErrorToast({ 
        message: 'La copie est désactivée pendant le quiz' 
      })
      if (onViolation) {
        onViolation('copy')
      }
      return false
    }

    // Désactiver l'impression
    const handleBeforePrint = () => {
      violationsRef.current++
      showErrorToast({ 
        message: 'L\'impression est désactivée pendant le quiz' 
      })
      if (onViolation) {
        onViolation('print')
      }
    }

    // Détecter les changements d'onglet/fenêtre
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsBlurred(true)
        blurTimeRef.current = Date.now()
        setTabSwitches(prev => prev + 1)
        violationsRef.current++
        
        if (onViolation) {
          onViolation('tab_switch')
        }
      } else {
        if (isBlurred && blurTimeRef.current) {
          const timeAway = Date.now() - blurTimeRef.current
          // Si l'utilisateur est parti plus de 5 secondes, c'est suspect
          if (timeAway > 5000) {
            setWarnings(prev => prev + 1)
            showErrorToast({ 
              message: `Attention : Changement d'onglet détecté (${Math.floor(timeAway / 1000)}s). Les triches peuvent entraîner l'annulation du quiz.` 
            })
          }
        }
        setIsBlurred(false)
        blurTimeRef.current = null
      }
    }

    // Détecter le blur de la fenêtre
    const handleBlur = () => {
      if (isActive) {
        setIsBlurred(true)
        blurTimeRef.current = Date.now()
        setTabSwitches(prev => prev + 1)
        violationsRef.current++
        
        if (onViolation) {
          onViolation('window_blur')
        }
      }
    }

    // Détecter le focus de la fenêtre
    const handleFocus = () => {
      if (isBlurred && blurTimeRef.current) {
        const timeAway = Date.now() - blurTimeRef.current
        if (timeAway > 5000) {
          setWarnings(prev => prev + 1)
          showErrorToast({ 
            message: `Attention : Changement de fenêtre détecté (${Math.floor(timeAway / 1000)}s). Les triches peuvent entraîner l'annulation du quiz.` 
          })
        }
      }
      setIsBlurred(false)
      blurTimeRef.current = null
    }

    // Ajouter les event listeners
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('selectstart', handleSelectStart)
    document.addEventListener('dragstart', handleDragStart)
    document.addEventListener('copy', handleCopy)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('beforeprint', handleBeforePrint)

    // Ajouter un style CSS pour désactiver la sélection
    const style = document.createElement('style')
    style.id = 'quiz-security-style'
    style.textContent = `
      body.quiz-active {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      body.quiz-active textarea,
      body.quiz-active input[type="text"],
      body.quiz-active input[type="textarea"] {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
      }
    `
    document.head.appendChild(style)
    document.body.classList.add('quiz-active')

    // Nettoyage
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('selectstart', handleSelectStart)
      document.removeEventListener('dragstart', handleDragStart)
      document.removeEventListener('copy', handleCopy)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('beforeprint', handleBeforePrint)
      
      const styleElement = document.getElementById('quiz-security-style')
      if (styleElement) {
        styleElement.remove()
      }
      document.body.classList.remove('quiz-active')
      
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current)
      }
    }
  }, [isActive, isBlurred, onViolation])

  // Détecter les tentatives d'ouverture de DevTools
  useEffect(() => {
    if (!isActive) return

    let devtools = { open: false }
    const threshold = 160

    const checkDevTools = () => {
      const widthThreshold = window.outerWidth - window.innerWidth > threshold
      const heightThreshold = window.outerHeight - window.innerHeight > threshold

      if (widthThreshold || heightThreshold) {
        if (!devtools.open) {
          devtools.open = true
          violationsRef.current++
          showErrorToast({ 
            message: 'L\'ouverture des outils de développement est détectée. Les triches peuvent entraîner l\'annulation du quiz.' 
          })
          if (onViolation) {
            onViolation('devtools_opened')
          }
        }
      } else {
        devtools.open = false
      }
    }

    const interval = setInterval(checkDevTools, 500)

    return () => {
      clearInterval(interval)
    }
  }, [isActive, onViolation])

  return {
    tabSwitches,
    warnings,
    violations: violationsRef.current,
    isBlurred
  }
}

